'use client';

import { useState, useMemo, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon, IconName } from '~/components/core/Icon';
import { colors, spacing } from '~/utils/tailwindUtils';
import Flex from '../flex/Flex';
import { useCurrentLocale } from '~/locales/client';
import { LocaleEnum } from '~/locales/resources';

export interface NumberInputProps {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  required?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: string;
  name?: string;
  placeholder?: string;
  classNames?: string;
  isDisabled?: boolean;
  verified?: boolean;
  loading?: boolean;
  autoComplete?: string;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * NumberInput component that allows the user to input only numeric values
 * @param name
 * @param placeholder
 * @param onChange
 * @param onBlur
 * @param value
 * @param isDisabled
 * @param classNames
 * @param verified
 * @param loading
 * @param min
 * @param max
 * @param step
 * @returns NumberInput component
 */
export const NumberInput = ({
  name,
  placeholder,
  onChange,
  onBlur,
  value,
  isDisabled,
  classNames,
  verified,
  loading,
  autoComplete,
  min,
  max,
  step,
}: NumberInputProps) => {
  const locale = useCurrentLocale();
  const [userInputValue, setUserInputValue] = useState<string | null>(null);

  // Create Intl.NumberFormat instance for the current locale (memoized)
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale === LocaleEnum.DE ? 'de-DE' : 'en-US', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 20, // Allow many decimal places
      }),
    [locale],
  );

  // Format number with thousand separators based on locale using Intl.NumberFormat
  const formatNumber = useCallback(
    (numStr: string): string => {
      if (!numStr || numStr === '' || numStr === '-') return numStr;

      // Remove all non-numeric characters except decimal point and minus sign
      const cleaned = numStr.replace(/[^\d.-]/g, '');
      if (cleaned === '' || cleaned === '-') return cleaned;

      // Parse to number
      const numValue = parseFloat(cleaned);
      if (isNaN(numValue)) return cleaned;

      // Format using Intl.NumberFormat
      let formatted = numberFormatter.format(numValue);

      // For German locale, Intl.NumberFormat uses comma as decimal separator
      // But we want to use dot as decimal separator to match the stored format
      // So we need to replace comma with dot for decimals
      if (locale === LocaleEnum.DE && cleaned.includes('.')) {
        // Check if there's a decimal part
        const parts = cleaned.split('.');
        if (parts.length === 2) {
          // Replace the comma decimal separator with dot
          formatted = formatted.replace(',', '.');
        }
      }

      // Handle negative sign
      if (cleaned.startsWith('-') && !formatted.startsWith('-')) {
        formatted = `-${formatted}`;
      }

      return formatted;
    },
    [numberFormatter, locale],
  );

  // Parse formatted number to raw numeric string (remove separators)
  const parseNumber = useCallback(
    (formattedStr: string): string => {
      if (!formattedStr || formattedStr === '') return '';

      // Get the locale-specific separators from Intl.NumberFormat
      const parts = numberFormatter.formatToParts(1234567.89);
      const thousandSeparator = parts.find((p) => p.type === 'group')?.value || '';
      const decimalSeparator = parts.find((p) => p.type === 'decimal')?.value || '';

      // Remove thousand separators
      let cleaned = formattedStr;
      if (thousandSeparator) {
        // Escape special regex characters
        const escapedSeparator = thousandSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        cleaned = cleaned.replace(new RegExp(escapedSeparator, 'g'), '');
      }

      // Replace locale-specific decimal separator with dot (our standard)
      if (decimalSeparator && decimalSeparator !== '.') {
        cleaned = cleaned.replace(decimalSeparator, '.');
      }

      // Clean any remaining non-numeric characters except dot and minus
      cleaned = cleaned.replace(/[^\d.-]/g, '');

      return cleaned;
    },
    [numberFormatter],
  );

  // Compute formatted value from prop
  const formattedPropValue = useMemo(() => {
    if (value !== undefined && value !== '') {
      return formatNumber(value);
    }
    return '';
  }, [value, formatNumber]);

  // Use user input if available, otherwise use formatted prop value
  const displayValue = userInputValue !== null ? userInputValue : formattedPropValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Parse the input to get raw numeric value
    const rawValue = parseNumber(inputValue);

    // Validate that it's a valid number format
    if (inputValue === '' || /^-?\d*\.?\d*$/.test(rawValue)) {
      // Store the formatted display value for user input
      const formatted = formatNumber(rawValue);
      setUserInputValue(formatted);

      // Create a new event with the raw value
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: rawValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Reset user input on blur so prop value takes precedence
    setUserInputValue(null);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point, and minus sign
    if (
      [46, 8, 9, 27, 13, 110, 190, 189].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  return (
    <Flex className="relative">
      <input
        name={name}
        id={name}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        autoComplete={autoComplete}
        min={min}
        max={max}
        step={step}
        className={twMerge(
          'bg-gray-50 hover:bg-gray-100 pr-3 pl-6 h-10 text-black border border-gray-300 rounded-lg text-xs md:text-sm font-medium w-full max-w-5xl focus:outline-none focus:border-gray-300 placeholder:text-xs',
          isDisabled && 'opacity-50 text-gray-500 font-normal hover:bg-gray-50 cursor-not-allowed',
          classNames,
        )}
      />
      {!loading && typeof verified === 'boolean' ? (
        <Icon
          name={verified ? IconName.CheckCircle : IconName.CancelCircle}
          color={verified ? colors.green[500] : colors.red[500]}
          size={spacing[6]}
          className="absolute right-2 top-2 border-l border-gray-200 pl-2"
        />
      ) : loading ? (
        <Icon
          name={IconName.Loader}
          size={spacing[5]}
          color={colors.gray[500]}
          className="absolute right-2 top-3 animate-spin"
        />
      ) : null}
    </Flex>
  );
};
